/**
 * Results Page: video player, final AI summary, clickable timeline,
 * timestamped transcript, frame gallery with per-frame analysis modal,
 * scene/people/object/event breakdowns, and report downloads.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import VideoPreview from '../components/VideoPreview';
import Timeline from '../components/Timeline';
import TranscriptView from '../components/TranscriptView';
import FrameGallery from '../components/FrameGallery';
import FrameModal from '../components/FrameModal';
import SummaryPanel from '../components/SummaryPanel';
import DownloadButtons from '../components/DownloadButtons';
import { getResults, videoStreamUrl } from '../api/api';

const TABS = ['Overview', 'Timeline', 'Transcript', 'Frames', 'Breakdown'];

export default function ResultsPage() {
  const { jobId } = useParams();
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('Overview');
  const [selectedFrame, setSelectedFrame] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getResults(jobId);
        if (!cancelled) setResult(data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const handleSeek = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime = seconds;
      videoRef.current.play().catch(() => {});
    }
  };

  if (error) {
    return <p className="text-red-600 text-center">{error}</p>;
  }
  if (!result) {
    return <p className="text-gray-400 text-center">Loading results…</p>;
  }

  const { finalAnalysis = {}, transcript = {}, frames = [], originalName } = result;
  const videoSrc = videoStreamUrl(jobId);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">Analysis Results</h1>
          <p className="text-gray-500 text-sm mt-0.5">{originalName}</p>
        </div>
        <DownloadButtons result={result} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <VideoPreview videoRef={videoRef} src={videoSrc} />

          <div className="bg-white rounded-xl border border-gray-200 shadow-card p-5 border-l-4 border-l-brand-600">
            <h2 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <svg viewBox="0 0 20 20" fill="none" className="w-4 h-4 text-brand-600">
                <path d="M3 10h14M3 5h14M3 15h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              Executive Summary
            </h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              {finalAnalysis.executive_summary || 'No summary generated.'}
            </p>
          </div>

          <div className="inline-flex flex-wrap gap-1 bg-gray-100 rounded-xl p-1">
            {TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? 'bg-white text-brand-700 shadow-soft'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {activeTab === 'Overview' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-card p-5">
              <h3 className="font-semibold mb-2">Overall Story</h3>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {finalAnalysis.overall_story || 'N/A'}
              </p>
            </div>
          )}

          {activeTab === 'Timeline' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-card p-5">
              <Timeline items={finalAnalysis.timeline} onSeek={handleSeek} />
            </div>
          )}

          {activeTab === 'Transcript' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-card p-5">
              <TranscriptView
                segments={transcript.segments}
                fullText={transcript.fullText}
                onSeek={handleSeek}
              />
            </div>
          )}

          {activeTab === 'Frames' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-card p-5">
              <FrameGallery jobId={jobId} frames={frames} onSelectFrame={setSelectedFrame} />
            </div>
          )}

          {activeTab === 'Breakdown' && <SummaryPanel finalAnalysis={finalAnalysis} />}
        </div>

        <aside className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-card p-5 sticky top-24">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <svg viewBox="0 0 20 20" fill="none" className="w-4 h-4 text-brand-600">
                <path d="M10 2v6l4 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.8" />
              </svg>
              Key Events
            </h3>
            <Timeline items={(finalAnalysis.events || []).slice(0, 5)} onSeek={handleSeek} importanceKey="importance" />
          </div>
        </aside>
      </div>

      <FrameModal jobId={jobId} frame={selectedFrame} onClose={() => setSelectedFrame(null)} />
    </div>
  );
}
